import { Box, Button, ColumnLayout, Container, FormField, Multiselect, MultiselectProps, SpaceBetween, Toggle } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import LastRefreshedAt from 'src/components/generic-components/LastRefreshedAt';
import { TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import { logger } from 'src/logger';
import { getMultiSelectPlaceHolderValue } from 'src/utilities/CommonUtilities';
import { getCurrentTime, getTimeDifference, sortedDateArrayMMMYYYYFormat } from 'src/utilities/DateTimeUtilities';
import { filtersLabel } from '../FinTechOpsCommonElements';
import { FinTechOpsMessages, GraphDateFormat_MMM_YY, MANAGER_ROSTER } from '../FinTechOpsConstants';
import {
  CreateBarChartData,
  CreateLineChartData,
  stringArrayToOptionsObj,
  optionsObjectToStringArray,
  parseObjectToMultiSelectOptions_Sorted,
  returnFilterAgeSorted,
  returnSelectedOptionsAge
} from '../FinTechOpsDataProcessingFunctions';
import { useUserMetricsContext } from './UserMetricsContext';
import {
  applyFiltersUserMetrics,
  createNonFinTechOrgData,
  createUserMetricsDetailsTabData,
  createUserMetricsGraphDataV2,
  createUserMetricsMasterFilterData,
  returnManagerRosterData,
  returnSelectedAllAppNameCustom
} from './UserMetricsDataProcessing';
import { UserMetricsMasterFilters, UserMetricsParsedData } from './UserMetricsInterfaces';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

export const UserMetricsSelectionFilters: React.FC = () => {
  const userMetricsContext = useUserMetricsContext();
  const finOpsContext = useFinTechContext();

  const [lastRefreshed, setLastRefreshed] = useState('');
  const [masterFilters, setMasterFilters] = useState<UserMetricsMasterFilters>({} as UserMetricsMasterFilters);

  // Current filter selections (bound to UI multiselects)
  const [appManagers, setAppManagers] = useState<MultiselectProps.Options>([]);
  const [team, setTeam] = useState<MultiselectProps.Options>([]);
  const [applicationType, setApplicationType] = useState<MultiselectProps.Options>([]);
  const [applicationName, setApplicationName] = useState<MultiselectProps.Options>([]);
  const [month, setMonth] = useState<MultiselectProps.Options>([]);
  const [userLogin, setUserLogin] = useState<MultiselectProps.Options>([]);
  const [userManagers, setUserManagers] = useState<MultiselectProps.Options>([]);

  // Track previously applied filters to detect changes and avoid unnecessary updates
  const [previouslyAppliedFilters, setPreviouslyAppliedFilters] = useState<string>('');

  const [includeOrg, setIncludeOrg] = useState(true);
  const [prevIncludeOrg, setPrevIncludeOrg] = useState(true);

  const [isResetFilter, setIsResetFilter] = useState(false);
  const [dataSubSetNoFinTech, setDataSubSetNoFinTech] = useState<UserMetricsParsedData[]>([]);
  const [dataSubSetFinTech, setDataSubSetFinTech] = useState<UserMetricsParsedData[]>([]);

  /**
   * Generates a JSON string signature of the current filter selections.
   * Used for comparing current vs previously applied filters to detect changes.
   */
  const getCurrentFilterSignature = (orgInclude: boolean): string => {
    return JSON.stringify({
      managers: optionsObjectToStringArray(appManagers).sort(),
      team: optionsObjectToStringArray(team).sort(),
      applicationType: optionsObjectToStringArray(applicationType).sort(),
      applicationName: optionsObjectToStringArray(applicationName).sort(),
      month: returnSelectedOptionsAge(month).sort(),
      userLogin: optionsObjectToStringArray(userLogin).sort(),
      userManagers: optionsObjectToStringArray(userManagers).sort(),
      orgInclude: orgInclude
    });
  };

  useEffect(() => {
    // Guard: Skip if this is the second trigger from setIsResetFilter(false) and not first load
    // When isResetFilter changes from true->false, it triggers useEffect again unnecessarily
    const isFirstLoad = !userMetricsContext.baselineMasterFilters?.user_login?.length;
    if (!isResetFilter && !isFirstLoad && finOpsContext.finTechOpsUserMetricsDataStatus === 'finished') {
      return;
    }

    userMetricsContext.setDetailsStatus('loading');
    userMetricsContext.setOverViewStatus('loading');
    if (finOpsContext.finTechOpsUserMetricsDataStatus == 'finished') {
      let initialFilters: UserMetricsMasterFilters = {
        team: [],
        managers: [],
        application_name: [],
        application_type: [],
        month: [],
        user_login: [],
        user_manager: [],
        org_include: true
      };

      // Check if baseline filters have been set in context (first load detection)
      const isFirstLoad = !userMetricsContext.baselineMasterFilters?.user_login?.length;

      if (isFirstLoad) {
        const [managerRosterData, userMetricsData]: [UserMetricsParsedData[], UserMetricsParsedData[]] = returnManagerRosterData(
          finOpsContext.userMetricsWithUserData.data
        );

        // Completely separate ManagerRoster Data from Regular User Metrics Data
        userMetricsContext.setManagerRosterMetricsData(managerRosterData);
        // IMPORTANT: this is the only point where we have metrics page alter higher level finOps Context
        finOpsContext.setUserMetricsWithUserData({ data: userMetricsData, metadata: finOpsContext.userMetricsWithUserData.metadata });

        // First data load - set baseline filters in context
        initialFilters = createUserMetricsMasterFilterData(userMetricsData);
        userMetricsContext.setBaselineMasterFilters(initialFilters);
        // Set original filter options from unfiltered baseline data
        userMetricsContext.setOriginalFilterOptions(JSON.stringify(initialFilters));
      }

      const allFilters =
        initialFilters.user_login.length > 0 ? initialFilters : getUserMetricsMasterFilters(finOpsContext.userMetricsWithUserData.data);
      const filtersJson = JSON.stringify(allFilters);

      // Update the filters' values
      if (userMetricsContext.setSummaryFilters) {
        userMetricsContext.setSummaryFilters(filtersJson);
      }
      const [finTechSubSet, nonFinTechSubSet] = createNonFinTechOrgData(finOpsContext.userMetricsWithUserData.data);
      setDataSubSetNoFinTech(nonFinTechSubSet);
      setDataSubSetFinTech(finTechSubSet);

      updateAllUserMetricsData(allFilters);

      // Set the initial filter signature after first load OR after clearing filters
      if (isFirstLoad || isResetFilter) {
        // Use setTimeout to ensure filter state updates have propagated before capturing signature
        setTimeout(() => {
          setPreviouslyAppliedFilters(getCurrentFilterSignature(includeOrg));
        }, 0);
      }

      setLastRefreshed(finOpsContext?.userMetricsWithUserData?.metadata?.last_updated_at);
      setIsResetFilter(false);
    }
    if (finOpsContext.finTechOpsUserMetricsDataStatus == 'error') {
      userMetricsContext.displayNotification(`error`, FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      userMetricsContext.setDetailsStatus('error');
      userMetricsContext.setOverViewStatus('error');
    }
  }, [finOpsContext.finTechOpsUserMetricsDataStatus, isResetFilter]);

  /*
   * Based on given items, it will get the unique string values for the keys associated with the
   * selection filters.
   */
  const getUserMetricsMasterFilters = (userMetricsData: UserMetricsParsedData[]): UserMetricsMasterFilters => {
    // Retrieve the unique string values associated with selection filters
    const getFilters: UserMetricsMasterFilters = createUserMetricsMasterFilterData(userMetricsData);

    // Setting the master filter value to the uniques values retrieved
    // Creating cloudscape conforming objects for use in the multiselect components
    setMasterFilters(getFilters);
    const _managers = stringArrayToOptionsObj(getFilters.managers);
    const _team = stringArrayToOptionsObj(getFilters.team);
    const _applicationType = stringArrayToOptionsObj(getFilters.application_type);
    const _applicationName = returnSelectedAllAppNameCustom(getFilters.application_name);
    const _month = stringArrayToOptionsObj(getFilters.month);
    const _userLogin = stringArrayToOptionsObj(getFilters.user_login);
    const _userManagers = stringArrayToOptionsObj(getFilters.user_manager);

    // Setting the multiselect option values to the objects created.
    setAppManagers(_managers);
    setTeam(_team);
    setApplicationType(_applicationType);
    setApplicationName(_applicationName);
    setMonth(_month);
    setUserLogin(_userLogin);
    setUserManagers(_userManagers);

    return {
      managers: getFilters.managers,
      team: getFilters.team,
      application_type: getFilters.application_type,
      application_name: getFilters.application_name,
      month: getFilters.month,
      user_login: getFilters.user_login,
      user_manager: getFilters.user_manager,
      org_include: includeOrg
    };
  };

  const updateAllUserMetricsData = (masterFilters: UserMetricsMasterFilters) => {
    userMetricsContext.setDetailsStatus('loading');
    userMetricsContext.setOverViewStatus('loading');
    try {
      const start = getCurrentTime();
      // Getting Filtered user metrics data with users grain
      const [filteredHistoricData, filteredRecentData]: [UserMetricsParsedData[], UserMetricsParsedData[]] = applyFiltersUserMetrics(
        masterFilters.org_include ? finOpsContext.userMetricsWithUserData.data : dataSubSetNoFinTech,
        masterFilters
      );

      // Set filtered data for hierarchy tab
      userMetricsContext.setFilteredUserMetricsData(filteredRecentData);

      if (masterFilters.org_include) {
        const [finTechSubSet, nonFinTechSubSet] = createNonFinTechOrgData(filteredRecentData);
        setDataSubSetNoFinTech(nonFinTechSubSet);
        setDataSubSetFinTech(finTechSubSet);
      }

      getUserMetricsMasterFilters(filteredRecentData);
      const newFilters = createUserMetricsMasterFilterData(filteredRecentData);
      userMetricsContext.setSummaryFilters(JSON.stringify(newFilters));

      const calculatedUserMetricsTableData = createUserMetricsDetailsTabData(filteredRecentData, filteredHistoricData, masterFilters.month);

      const calculatedUserMetricsGraphData = createUserMetricsGraphDataV2(filteredRecentData);

      const totalApplicationViewsByTeamGraph = CreateBarChartData(calculatedUserMetricsGraphData.appViewerShipTeamGraph);
      const top5ApplicationViews = CreateLineChartData(calculatedUserMetricsGraphData.top5AppViewershipGraph, GraphDateFormat_MMM_YY, 'month', false);
      const totalUniqueViewsGraph = CreateLineChartData(calculatedUserMetricsGraphData.uniqueUsersByAppGraph, GraphDateFormat_MMM_YY, 'month', false);
      const totalViewsGraph = CreateLineChartData(calculatedUserMetricsGraphData.totalAppViewsGraph, GraphDateFormat_MMM_YY, 'month', false);

      userMetricsContext.setAppViewershipByTeamData(totalApplicationViewsByTeamGraph.dataSeries);
      userMetricsContext.setAppViewershipByTeamXDomain(totalApplicationViewsByTeamGraph.xDomain);
      userMetricsContext.setAppViewershipByTeamYDomain(totalApplicationViewsByTeamGraph.yDomain);

      userMetricsContext.setTop5AppsViewership(top5ApplicationViews.dataSeries);
      userMetricsContext.setTop5AppsViewershipXDomain(top5ApplicationViews.xDomain);
      userMetricsContext.setTop5AppsViewershipYDomain(top5ApplicationViews.yDomain);

      userMetricsContext.setUniqueUsersByApplication(totalUniqueViewsGraph.dataSeries);
      userMetricsContext.setUniqueUsersByApplicationXDomain(totalUniqueViewsGraph.xDomain);
      userMetricsContext.setUniqueUsersByApplicationYDomain(totalUniqueViewsGraph.yDomain);

      userMetricsContext.setTotalApplicationViews(totalViewsGraph.dataSeries);
      userMetricsContext.setTotalApplicationViewsXDomain(totalViewsGraph.xDomain);
      userMetricsContext.setTotalApplicationViewsYDomain(totalViewsGraph.yDomain);

      userMetricsContext.setAggregatedViewsTableData(calculatedUserMetricsTableData.aggregatedViewsTable);
      userMetricsContext.setApplicationUsersTableData(calculatedUserMetricsTableData.aggregatedUsersTable);

      userMetricsContext.setAggregatedSubApplicationUniqueUsersTableData(calculatedUserMetricsTableData.aggregatedSubApplicationUniqueUsersTableData);
      userMetricsContext.setAggregatedSubApplicationViewsTableData(calculatedUserMetricsTableData.aggregatedSubApplicationViewsTableData);

      //Setting table data for application footprint details
      userMetricsContext.setApplicationFootprintTableData(calculatedUserMetricsTableData.applicationFootprintTable);
      //Setting table data for sub application footprint details
      userMetricsContext.setSubApplicationFootprintTableData(calculatedUserMetricsTableData.subApplicationFootprintTable);
      userMetricsContext.setApplicationFootprintContainer(calculatedUserMetricsTableData.applicationFootprintContainer);
      //Setting table data for application user view details
      userMetricsContext.setApplicationUserViewTableData(calculatedUserMetricsTableData.applicationUserViewTable);
      //Setting table data for sub application user view details
      userMetricsContext.setSubApplicationUserViewTableData(calculatedUserMetricsTableData.subApplicationUserViewTable);

      userMetricsContext.setDetailsStatus('finished');
      userMetricsContext.setOverViewStatus('finished');
      const elapsed = getTimeDifference(start);
      logger.info(`Fetched calculated data for UserMetrics in ${elapsed} ms`);
    } catch (error: any) {
      logger.error('Unable to Process User Metrics Data', error);
      userMetricsContext.displayNotification(`error`, FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      userMetricsContext.setDetailsStatus('error');
      userMetricsContext.setOverViewStatus('error');
    }
  };

  const triggerCall = (checked?: boolean) => {
    if (checked === undefined) {
      checked = includeOrg;
    }

    // Generate signature of current filter state
    const currentSignature = getCurrentFilterSignature(checked);

    // Skip update if nothing changed
    if (currentSignature === previouslyAppliedFilters) {
      logger.info('Filter selections unchanged, skipping data refresh');
      return;
    }

    let finTechMasterFilterData: UserMetricsMasterFilters = {
      managers: [],
      team: [],
      application_type: [],
      application_name: [],
      month: [],
      user_login: [],
      user_manager: [],
      org_include: true
    };

    if (checked && !prevIncludeOrg) {
      finTechMasterFilterData = createUserMetricsMasterFilterData(dataSubSetFinTech);
    }
    const managerStr = optionsObjectToStringArray(appManagers);
    const teamStr = optionsObjectToStringArray(team);
    const applicationTypeStr = optionsObjectToStringArray(applicationType);
    const applicationNameStr = optionsObjectToStringArray(applicationName);
    const monthStr = returnSelectedOptionsAge(month);
    const userLoginStr = optionsObjectToStringArray(userLogin);
    const userManagerStr = optionsObjectToStringArray(userManagers);

    updateAllUserMetricsData({
      managers: managerStr.concat(finTechMasterFilterData.managers),
      team: teamStr.concat(finTechMasterFilterData.team),
      application_type: applicationTypeStr.concat(finTechMasterFilterData.application_type),
      application_name: applicationNameStr.concat(finTechMasterFilterData.application_name),
      month: monthStr.concat(finTechMasterFilterData.month),
      user_login: userLoginStr.concat(finTechMasterFilterData.user_login),
      user_manager: userManagerStr.concat(finTechMasterFilterData.user_manager),
      org_include: checked
    });

    // Store the new signature after successful update
    setPreviouslyAppliedFilters(currentSignature);
    setPrevIncludeOrg(checked);
  };

  return (
    <div>
      <SpaceBetween size="l">
        <Box>
          <SpaceBetween size="s">
            <Container
              fitHeight
              header={
                <SpaceBetween size="m" direction="horizontal" alignItems="center">
                  <Box variant="h2">{Titles.TITLE_USER_METRICS}</Box>
                  <LastRefreshedAt lastRefreshedDateTime={lastRefreshed} />
                  <Button onClick={() => setIsResetFilter(true)}>Clear Filters</Button>
                  <Toggle
                    checked={includeOrg}
                    onChange={({ detail }) => {
                      setIncludeOrg(detail.checked);
                      // Trigger data refresh after state updates
                      setTimeout(() => triggerCall(detail.checked), 0);
                    }}
                  >
                    Include DaS-FinTech
                  </Toggle>
                </SpaceBetween>
              }
            >
              <Box>
                <ColumnLayout columns={4} minColumnWidth={20}>
                  <FormField label={filtersLabel('Team')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(team, 'Team', userMetricsContext.baselineMasterFilters?.team || [])}
                      options={parseObjectToMultiSelectOptions_Sorted(masterFilters.team, 'All Teams')}
                      selectedOptions={team}
                      onChange={({ detail }) => setTeam(detail.selectedOptions)}
                      onBlur={() => triggerCall()}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading Teams"
                      statusType={finOpsContext.finTechOpsUserMetricsDataStatus}
                      disabled={userMetricsContext.overViewStatus === 'loading' || userMetricsContext.detailsStatus === 'loading' ? true : false}
                    />
                  </FormField>

                  <FormField label={filtersLabel('Managers')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(appManagers, 'Managers', userMetricsContext.baselineMasterFilters?.managers || [])}
                      options={parseObjectToMultiSelectOptions_Sorted(masterFilters.managers, 'All Managers')}
                      selectedOptions={appManagers}
                      onChange={({ detail }) => setAppManagers(detail.selectedOptions)}
                      onBlur={() => triggerCall()}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading Managers"
                      statusType={finOpsContext.finTechOpsUserMetricsDataStatus}
                      disabled={userMetricsContext.overViewStatus === 'loading' || userMetricsContext.detailsStatus === 'loading' ? true : false}
                    />
                  </FormField>

                  <FormField label={filtersLabel('Application')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(
                        applicationType,
                        'Application',
                        userMetricsContext.baselineMasterFilters?.application_type || []
                      )}
                      options={parseObjectToMultiSelectOptions_Sorted(masterFilters.application_type, 'All Applications')}
                      selectedOptions={applicationType}
                      onChange={({ detail }) => setApplicationType(detail.selectedOptions)}
                      onBlur={() => triggerCall()}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading Application"
                      statusType={finOpsContext.finTechOpsUserMetricsDataStatus}
                      disabled={userMetricsContext.overViewStatus === 'loading' || userMetricsContext.detailsStatus === 'loading' ? true : false}
                    />
                  </FormField>

                  <FormField label={filtersLabel('Sub Application')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(
                        applicationName,
                        'Sub Application',
                        userMetricsContext.baselineMasterFilters?.application_name || []
                      )}
                      options={parseObjectToMultiSelectOptions_Sorted(masterFilters.application_name, 'All Sub Application')}
                      selectedOptions={applicationName}
                      onChange={({ detail }) => setApplicationName(detail.selectedOptions)}
                      onBlur={() => triggerCall()}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading Sub Application"
                      statusType={finOpsContext.finTechOpsUserMetricsDataStatus}
                      disabled={userMetricsContext.overViewStatus === 'loading' || userMetricsContext.detailsStatus === 'loading' ? true : false}
                    />
                  </FormField>

                  <FormField label={filtersLabel('Month')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(month, 'Months', userMetricsContext.baselineMasterFilters?.month || [])}
                      options={returnFilterAgeSorted(sortedDateArrayMMMYYYYFormat(masterFilters.month), 'All Months')}
                      selectedOptions={month}
                      onChange={({ detail }) => setMonth(detail.selectedOptions)}
                      onBlur={() => triggerCall()}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading Months"
                      statusType={finOpsContext.finTechOpsUserMetricsDataStatus}
                      disabled={userMetricsContext.overViewStatus === 'loading' || userMetricsContext.detailsStatus === 'loading' ? true : false}
                    />
                  </FormField>

                  <FormField label={filtersLabel('User Login')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(
                        userLogin,
                        'User Login',
                        userMetricsContext.baselineMasterFilters?.user_login || []
                      )}
                      options={parseObjectToMultiSelectOptions_Sorted(masterFilters.user_login, 'All User Login')}
                      selectedOptions={userLogin}
                      onChange={({ detail }) => setUserLogin(detail.selectedOptions)}
                      onBlur={() => triggerCall()}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading User Login"
                      statusType={finOpsContext.finTechOpsUserMetricsDataStatus}
                      disabled={userMetricsContext.overViewStatus === 'loading' || userMetricsContext.detailsStatus === 'loading' ? true : false}
                    />
                  </FormField>
                  <FormField label={filtersLabel('User Leadership Hierarchy')}>
                    <Multiselect
                      placeholder={getMultiSelectPlaceHolderValue(
                        userManagers,
                        'User Managers',
                        userMetricsContext.baselineMasterFilters?.user_manager || []
                      )}
                      options={parseObjectToMultiSelectOptions_Sorted(masterFilters.user_manager, 'All User Managers')}
                      selectedOptions={userManagers}
                      onChange={({ detail }) => setUserManagers(detail.selectedOptions)}
                      onBlur={() => triggerCall()}
                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                      selectedAriaLabel="Selected"
                      expandToViewport
                      hideTokens
                      virtualScroll
                      filteringType="auto"
                      loadingText="Loading User Managers"
                      statusType={finOpsContext.finTechOpsUserMetricsDataStatus}
                      disabled={userMetricsContext.overViewStatus === 'loading' || userMetricsContext.detailsStatus === 'loading' ? true : false}
                    />
                  </FormField>
                </ColumnLayout>
              </Box>
            </Container>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </div>
  );
};
